import { observeControlValue$ } from '@/utils/angular/forms/reactive-forms/reactive-forms-rxjs-interop.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { afterNextRender, DestroyRef, Directive, effect, inject, Injector, input, linkedSignal, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgControl, TouchedChangeEvent } from '@angular/forms';
import type { ValidationError, WithOptionalFieldTree } from '@angular/forms/signals';
import { MatFormFieldControl } from '@angular/material/form-field';
import { isEqual } from 'lodash-es';
import { filter, merge, NEVER, type Observable, startWith, Subject, switchMap } from 'rxjs';

export abstract class UiFormFieldControl {
    /**
     * These functions can be reactive functions and the form fields components will react to their changes.
     * You can still use the stateChanges observables to mark changes if your functions are not reactive.
     */

    /**
     * Properties that are optional in FormUiControl (FormValueControl / FormCheckboxControl)
     * But that we require to be defined to interact with the form field
     */
    disabled!: () => boolean;
    required!: () => boolean;
    touched!: () => boolean;
    errors!: () => readonly WithOptionalFieldTree<ValidationError>[];

    /**
     * Properties exclusive to the UiFormFieldControl
     */
    shouldLabelFloat!: () => boolean;
    focused!: () => boolean;
    clearable!: () => boolean;
    dynamicHeight!: () => boolean;

    /**
     * Exclusive methods
     */
    onContainerClick!: (event: MouseEvent) => void;
    onClearRequest!: () => void;

    /**
     * This fires everytime the state of the control changes so that the consummer knows it has to update
     * Prefer making your functions reactive, this to ensure retrocompat of old components.
     */
    stateChanges?: Observable<void>;
}

/**
 * This directive is to be inserted inside a mat form field which is looking for a MatFormFieldControl in its content
 * It maps a UiFormFieldControl (with a reactive interface) to a MatFormFieldControl (a legacy static fields + changes notifier interface)
 */
@Directive({
    selector: '[uiFormFieldControlToMatFormFieldControl]',
    providers: [{ provide: MatFormFieldControl, useExisting: UiFormFieldControlToMatFormFieldControlDirective }]
})
export class UiFormFieldControlToMatFormFieldControlDirective implements MatFormFieldControl<unknown> {
    control = input.required<UiFormFieldControl>({ alias: 'uiFormFieldControlToMatFormFieldControl' });
    controlStateChanges = signal<void>(undefined, { equal: () => false });

    // These linked signals will be recomputed when either a dependency of the reactive function emits a change
    // or when the control stateChanges observable emits

    controlDisabled = linkedSignal(() => {
        this.controlStateChanges();
        return this.control().disabled();
    });

    controlErrorState = linkedSignal(() => {
        this.controlStateChanges();
        return this.control().errors().length > 0 && this.control().touched();
    });

    controlFocused = linkedSignal(() => {
        this.controlStateChanges();
        return this.control().focused();
    });

    controlRequired = linkedSignal(() => {
        this.controlStateChanges();
        return this.control().required();
    });

    controlShouldLabelFloat = linkedSignal(() => {
        this.controlStateChanges();
        return this.control().shouldLabelFloat();
    });

    value: any = null;
    empty = true;
    id = null as unknown as string;
    placeholder = 'dummy value'; // a value is required for floating labels to work
    ngControl: null = null;
    setDescribedByIds = () => {};

    stateChanges = new Subject<void>();
    disabled = false;
    errorState = false;
    focused = false;
    required = false;
    shouldLabelFloat = false;

    constructor() {
        effect(() => {
            this.disabled = this.controlDisabled();
            this.errorState = this.controlErrorState();
            this.focused = this.controlFocused();
            this.required = this.controlRequired();
            this.shouldLabelFloat = this.controlShouldLabelFloat();
            this.stateChanges.next();
        });

        toObservable(this.control)
            .pipe(switchMap(control => control.stateChanges ?? NEVER))
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                this.controlStateChanges.set();
            });
    }

    onContainerClick(event: MouseEvent) {
        this.control().onContainerClick(event);
    }
}

/**
 * Helper for components that still implement ControlValueAccessor instead of FormValueControl
 * Replace this with inputs / models when updating the component to FormValueControl
 */
export function bindErrorsToNgControl(
    getChangesNotTrackedByNgControl$: () => Observable<any> = () => NEVER
): Signal<readonly WithOptionalFieldTree<ValidationError>[]> {
    const injector = inject(Injector);
    const destroyRef = inject(DestroyRef);

    const errors = signal<readonly WithOptionalFieldTree<ValidationError>[]>([], { equal: isEqual });

    afterNextRender(() => {
        const ngControl = injector.get(NgControl);

        if (!ngControl.control) {
            return;
        }

        merge(observeControlValue$(ngControl.control), getChangesNotTrackedByNgControl$())
            .pipe(startWith(null), takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                if (!ngControl.errors) {
                    return errors.set([]);
                } else {
                    return errors.set(
                        Object.entries(ngControl.errors)
                            .map(([key, value]) => {
                                if (value) {
                                    return {
                                        kind: key,
                                        message: undefined // try to fill this message automatically when working on error fields
                                    } satisfies WithOptionalFieldTree<ValidationError>;
                                }
                            })
                            .filter(isNotNullish)
                    );
                }
            });
    });

    return errors.asReadonly();
}

/**
 * Helper for components that still implement ControlValueAccessor instead of FormValueControl
 * Replace this with inputs / models when updating the component to FormValueControl
 */
export function bindTouchedToNgControl(getChangesNotTrackedByNgControl$: () => Observable<any> = () => NEVER): Signal<boolean> {
    const injector = inject(Injector);
    const destroyRef = inject(DestroyRef);
    const touched = signal<boolean>(false);

    afterNextRender(() => {
        const ngControl = injector.get(NgControl);

        merge(
            ngControl.control?.events.pipe(filter(event => event instanceof TouchedChangeEvent)) ?? NEVER,
            getChangesNotTrackedByNgControl$()
        )
            .pipe(startWith(null), takeUntilDestroyed(destroyRef))
            .subscribe(() => {
                touched.set(ngControl.touched ?? false);
            });
    });

    return touched.asReadonly();
}
